import React, { useState, ReactElement, ChangeEvent } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, FormControlLabel, Switch,
  TextField, ToggleButton, ToggleButtonGroup
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { cloneObject } from "../../../system/utils";
import { IDashboard } from "../types";
import { getPalette } from "../../../configuration/configuration";

interface IProps {
  dashboard: IDashboard;
  setDashboard: (dashboard: IDashboard) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface IDesign {
  title: string;
  colorIndex: number;
  isPrivate: boolean;
  isDefault: boolean;
  isEditable: boolean;
  autoStart: boolean;
  range: number;
  fontSize: number;
  scan: number;
  rows?: number;
  columns?: number;
}

const generateDesignData = (dashboard: IDashboard): IDesign => {

  return  {
    title: dashboard.name,
    isPrivate: dashboard.isPrivate ?? true,
    isDefault: dashboard.isDefault ?? false,
    isEditable: dashboard.isEditable ?? false,
    autoStart: dashboard.customConfig.autoStart ?? false,
    range: dashboard.customConfig.range ?? 1,
    fontSize: dashboard.customConfig.fontSize ?? 1,
    scan: dashboard.customConfig.scan ?? 1000,
    rows: dashboard.customConfig.rows ?? 12,
    columns: dashboard.customConfig.columns ?? 12,
    colorIndex: dashboard.customConfig.colorIndex ?? 0
  };
};


const MainDialog = ({ dashboard, setDashboard, open, setOpen }: IProps): ReactElement => {
  const [ design, setDesign ] = useState<IDesign>(generateDesignData(dashboard));

  const onchangeTitle = (event: ChangeEvent<HTMLInputElement>) => {
    if (design) {
      setDesign({ ...design, title: event.target.value });
    }
  };

  const onAccept = () => {
    if (!design) {
      return;
    }
    const newDashboard = cloneObject(dashboard);

    newDashboard.name = design.title;
    newDashboard.isPrivate = design.isPrivate;
    newDashboard.isDefault = design.isDefault;
    newDashboard.isEditable = design.isEditable;
    newDashboard.customConfig.range = design.range;
    newDashboard.customConfig.fontSize = design.fontSize;
    newDashboard.customConfig.scan = design.scan;
    newDashboard.customConfig.rows = design.rows;
    newDashboard.customConfig.columns = design.columns;
    newDashboard.customConfig.colorIndex = design.colorIndex;
    newDashboard.customConfig.autoStart = design.autoStart;

    setDashboard(newDashboard);
    setOpen(false);
  };

  const onCancel = () => {
    setOpen(false);
    setDesign(generateDesignData(dashboard));
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDesign({ ...design, [event.target.name]: event.target.checked });
  };

  const handleRange = (_event: React.MouseEvent<HTMLElement>, value: number | null) => {
    const newValue = cloneObject(design);
    newValue.range = value ?? 1;
    setDesign(newValue);
  };

  const onchangeFontSize = (_event: React.MouseEvent<HTMLElement>, value: number | null) => {
    const newValue = cloneObject(design);
    newValue.fontSize = value ?? 1;
    setDesign(newValue);
  };

  const handleScan = (_event: React.MouseEvent<HTMLElement>, value: number | null) => {
    const newValue = cloneObject(design);
    newValue.scan = value ?? 1000;
    setDesign(newValue);
  };

  const onChangeLayout = (field: string, value: number) => {
    const newValue = cloneObject(design) as any;
    newValue[field] = value;
    setDesign(newValue);
  };

  const onSelectColorIndex = (value: number) => {
    const newValue = cloneObject(design) as any;
    newValue.colorIndex = value;
    setDesign(newValue);
  };

  return(
    <Dialog onClose={onCancel} aria-labelledby="customized-dialog-title" open={open} maxWidth="md">

      <DialogTitle id="customized-dialog-title">
        Layout Config
      </DialogTitle>

      <DialogContent dividers={true} >
        <div className="display-flex-column p-2">
          <TextField
            variant="standard"
            placeholder={"Title"}
            label={"Title"}
            value={design.title}
            onChange={onchangeTitle}/>

          <div className="display-flex-row">
            <FormControlLabel
              control={<Switch checked={design.isPrivate} onChange={handleChange} name="isPrivate"/>}
              label="Private"
            />
            <div className="flex-grow-1"/>
            <FormControlLabel
              labelPlacement="start"
              disabled={design.isPrivate}
              control={<Switch checked={design.isEditable} onChange={handleChange} name="isEditable"/>}
              label="Allow Edit"
            />
          </div>

          <div className="display-flex-row">
            <FormControlLabel
              control={<Switch checked={design.isDefault} onChange={handleChange} name="isDefault"/>}
              label="Default"
            />
            <div className="flex-grow-1"/>
            <FormControlLabel
              labelPlacement="start"
              control={<Switch checked={design.autoStart} onChange={handleChange} name="autoStart"/>}
              label="Auto Start"
            />
          </div>

          <FormControlLabel
            control={
              <div className="m-2 display-flex-row gap-1">
                {getPalette().rgbColor.map((c, index) => {

                  return (
                    <div key={`color-${index}`}
                      onClick={() => onSelectColorIndex(index)}
                      style={{
                        background: `rgb(${c})`,
                        width: 24,
                        height: 24,
                        border: design.colorIndex === index ? "1px solid whitesmoke" : undefined,
                        borderRadius: 4
                      }}/>
                  );
                })}
              </div>
            }
            label="Color"
          />

          <FormControlLabel
            control={
              <ToggleButtonGroup
                className="mr-2 ml-2"
                value={design.range ?? 1}
                exclusive={true}
                onChange={handleRange}>
                <ToggleButton value={0.25}>15 min</ToggleButton>
                <ToggleButton value={0.5}>30 min</ToggleButton>
                <ToggleButton value={1}>1 hr</ToggleButton>
                <ToggleButton value={2}>2 hrs</ToggleButton>
                <ToggleButton value={4}>4 hrs</ToggleButton>
                <ToggleButton value={8}>8 hrs</ToggleButton>
                <ToggleButton value={12}>12 hrs</ToggleButton>
              </ToggleButtonGroup>
            }
            label="Range"
          />

          <FormControlLabel
            control={
              <ToggleButtonGroup
                className="mr-2 ml-2 mt-1"
                value={design.fontSize ?? 1}
                exclusive={true}
                onChange={onchangeFontSize}>
                <ToggleButton value={1}>1</ToggleButton>
                <ToggleButton value={2}>2</ToggleButton>
                {/*<ToggleButton value={3} >3</ToggleButton>*/}
                {/*<ToggleButton value={4} >4</ToggleButton>*/}
              </ToggleButtonGroup>
            }
            label="Scale"
          />

          <FormControlLabel
            control={
              <ToggleButtonGroup
                className="mr-2 ml-2  mt-1"
                value={design.scan ?? 1000}
                exclusive={true}
                onChange={handleScan}>
                <ToggleButton value={100}>100 ms</ToggleButton>
                <ToggleButton value={500}>500 ms</ToggleButton>
                <ToggleButton value={1000}>1 s</ToggleButton>
                <ToggleButton value={5000}>5 s</ToggleButton>
                <ToggleButton value={10000}>10 s</ToggleButton>
              </ToggleButtonGroup>
            }
            label="Scan"
          />

          <div className="display-flex-row w-100 gap-2 mt-2 align-items-center">
            <strong>Layout</strong>
            <TextField
              variant="standard"
              placeholder={"Columns"}
              label={"Columns"}
              value={design.columns ?? 12}
              type="number"
              slotProps={{ input: { inputProps: { min: 4, max: 12 } }}}
              onChange={e => onChangeLayout("columns", parseInt(e.target.value, 10))}/>

            <TextField
              variant="standard"
              placeholder={"Rows"}
              label={"Rows"}
              value={design.rows ?? 12}
              type="number"
              slotProps={{ input: { inputProps: { min: 4, max: 12 } }}}
              onChange={e => onChangeLayout("rows", parseInt(e.target.value, 10))}/>

          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onAccept} variant="contained" color="primary" startIcon={<CheckIcon />}>
          Accept
        </Button>
        <Button onClick={onCancel} variant="contained" color="secondary" startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MainDialog;
